<template>
  <div class="cmaker-run-detail">
    <header class="page-hero">
      <div>
        <h4 class="page-hero__title mb-1">
          Run #{{ jobId }}
        </h4>
        <p class="page-hero__subtitle mb-0">
          <span v-if="run">
            {{ run.streamName || ('stream #' + run.streamId) }} ·
            {{ run.trigger || '—' }} · {{ run.mode || '—' }}
          </span>
        </p>
      </div>
      <div class="d-flex gap-2">
        <router-link to="/cmaker/runs" class="btn btn-light btn-sm page-hero__back">
          <i class="bi bi-arrow-left me-1"></i>К списку
        </router-link>
      </div>
    </header>

    <div v-if="loading" class="card-load-state">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

    <div v-else class="detail-grid">

      <!-- Meta card -->
      <section class="detail-card detail-card--full summary-card">
        <div class="summary-card__head">
          <div>
            <div class="generation-cloud-title">Сводка</div>
            <div class="summary-card__tags">
              <span class="summary-tag">
                <i class="bi bi-play-circle"></i>
                {{ run.trigger || '—' }}
              </span>
              <span class="summary-tag">
                <i class="bi bi-sliders"></i>
                {{ run.mode || '—' }}
              </span>
            </div>
          </div>
          <div class="summary-card__status">
            <span :class="['status-badge', `status-badge--${(run.jobStatus || '').toLowerCase()}`]">
              {{ run.jobStatus || '—' }}
            </span>
            <span v-if="showCmakerStatus" class="text-muted small">{{ run.cmakerStatus }}</span>
          </div>
        </div>

        <div class="summary-id">
          <i class="bi bi-box-seam summary-id__icon"></i>
          <span class="meta-label">cmaker run</span>
          <span class="summary-id__value" :title="run.cmakerRunId || ''">{{ run.cmakerRunId || '—' }}</span>
        </div>

        <div class="summary-details">
          <div class="summary-timing">
            <div class="meta-label">Тайминг</div>
            <div class="timing-list">
              <div class="timing-row"><span>Создан</span><time>{{ formatDate(run.createdAt) || '—' }}</time></div>
              <div class="timing-row"><span>Старт</span><time>{{ formatDate(run.startedAt) || '—' }}</time></div>
              <div class="timing-row"><span>Финиш</span><time>{{ formatDate(run.finishedAt) || '—' }}</time></div>
            </div>
          </div>

          <div class="summary-metrics">
            <div class="summary-metric">
              <i class="bi bi-stopwatch summary-metric__icon"></i>
              <div>
                <span class="meta-label">Длительность</span>
                <div class="summary-metric__value">{{ formatDuration(run.startedAt, run.finishedAt) }}</div>
              </div>
            </div>
            <div class="summary-metric">
              <i class="bi bi-cash-coin summary-metric__icon"></i>
              <div>
                <span class="meta-label">Стоимость</span>
                <div class="summary-metric__value">
                  <span v-if="run.costTotal != null">
                    {{ Number(run.costTotal).toFixed(4) }} <span class="summary-metric__unit">{{ run.costCurrency || '' }}</span>
                  </span>
                  <span v-else class="text-muted">—</span>
                </div>
              </div>
            </div>
            <div v-if="isActive" class="summary-metric summary-metric--action">
              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                :disabled="cancelling"
                @click="onCancel"
              >
                <span v-if="cancelling" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="bi bi-stop-circle me-1"></i>
                Отменить run
              </button>
            </div>
            <div v-if="canRerun" class="summary-rerun">
              <label class="meta-label">Перезапустить с шага</label>
              <div class="rerun-controls">
                <select
                  class="form-select form-select-sm rerun-select"
                  v-model="selectedRerunStep"
                  :disabled="!availableSteps.length || rerunning"
                >
                  <option v-if="!availableSteps.length" value="">Нет доступных шагов</option>
                  <option v-for="s in availableSteps" :key="s" :value="s">{{ s }}</option>
                </select>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm rerun-button"
                  :disabled="!selectedRerunStep || rerunning"
                  @click="onRerun"
                >
                  <span v-if="rerunning" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-arrow-clockwise me-1"></i>
                  Перезапустить
                </button>
              </div>
            </div>
            <div v-if="run.contentId" class="summary-metric">
              <i class="bi bi-file-earmark-text summary-metric__icon"></i>
              <div>
                <span class="meta-label">Контент</span>
                <div class="summary-metric__value">
                  <router-link :to="`/streams/${run.streamId}/content/${run.contentId}/edit`">
                    #{{ run.contentId }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="run.lastErrorCode || run.failedStep" class="summary-error">
          <i class="bi bi-exclamation-circle"></i>
          <div v-if="run.lastErrorCode">
            <span class="meta-label">Ошибка</span>
            <div>{{ run.lastErrorCode }}</div>
          </div>
          <div v-if="run.failedStep">
            <span class="meta-label">Упал на шаге</span>
            <div>{{ run.failedStep }}</div>
          </div>
          <div v-if="run.failureReason">
            <span class="meta-label">Причина</span>
            <div>{{ run.failureReason }}</div>
          </div>
        </div>

        <div v-if="isTerminal" class="summary-actions">
          <button
            v-if="isTerminal"
            type="button"
            class="btn btn-outline-danger btn-sm"
            :disabled="deleting"
            @click="onDelete"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-trash me-1"></i>
            Удалить run
          </button>
        </div>
      </section>

      <!-- Failure reason -->
      <section v-if="run.failureResult" class="detail-card detail-card--full failure-card">
        <div class="generation-cloud-title">Причина падения</div>
        <pre class="json-block">{{ pretty(run.failureResult) }}</pre>
      </section>

      <!-- Skip reason (editorial gate decided the topic wasn't worth writing) -->
      <section v-if="run.skipReason || run.skipCategory" class="detail-card detail-card--full skip-card">
        <div class="generation-cloud-title">Причина пропуска (skip)</div>
        <div v-if="run.skipCategory" class="mb-1"><span class="text-muted small">Категория:</span> {{ run.skipCategory }}</div>
        <div v-if="run.skipReason">{{ run.skipReason }}</div>
      </section>

      <details class="detail-card snapshot-card">
        <summary class="snapshot-card__summary">
          <span class="generation-cloud-title">Snapshots</span>
          <i class="bi bi-chevron-down snapshot-card__chevron"></i>
        </summary>
        <div class="snapshot-card__content snapshot-content-grid">
          <!-- Run config snapshot -->
          <section class="snapshot-pane">
            <div class="snapshot-pane__title">Snapshot конфига</div>
            <pre v-if="run.runConfigSnapshot" class="json-block">{{ pretty(run.runConfigSnapshot) }}</pre>
            <div v-else class="text-muted small">Конфиг не сохранён.</div>
          </section>

          <!-- Feeds snapshot -->
          <section class="snapshot-pane">
            <div class="snapshot-pane__title">Snapshot источников</div>
            <div v-if="feedsArray.length" class="feeds-wrap">
              <table class="table table-sm mb-0">
                <thead>
                  <tr><th>feed_id</th><th>RSS</th><th>URL</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(f, i) in feedsArray" :key="i">
                    <td>{{ f.feed_id || f.feedId || '—' }}</td>
                    <td>{{ f.name || ('rss #' + (f.rss_channel_id || f.rssChannelId)) }}</td>
                    <td class="text-muted small">{{ f.url }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted small">Источники не сохранены.</div>
          </section>
        </div>
      </details>

      <!-- Event log -->
      <section class="detail-card detail-card--full event-card">
        <div class="d-flex justify-content-between align-items-center">
          <div class="generation-cloud-title">Лог событий</div>
          <span v-if="isActive" class="text-success small">
            <i class="bi bi-broadcast me-1"></i>live
          </span>
          <span v-else class="text-muted small">archive</span>
        </div>
        <div class="event-log" ref="eventLogRef">
          <div v-for="(e, idx) in events" :key="idx" class="event-row">
            <div class="event-row__head">
              <span class="event-row__name">{{ e.eventName || e.event || 'event' }}</span>
              <span v-if="e.timestamp" class="event-row__time text-muted">{{ formatDate(e.timestamp) }}</span>
            </div>
            <pre v-if="e.dataJson || e.data" class="event-row__data">{{ pretty(e.dataJson || e.data) }}</pre>
          </div>
          <div v-if="!events.length" class="text-muted small p-3">События отсутствуют.</div>
        </div>
      </section>

    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getRun, getRunEvents, cancelRun, rerunStep, deleteRun } from '@/services/cmakerRunService'

const route = useRoute()
const router = useRouter()
// Reject the literal strings "undefined"/"null" that leak in from bad route params,
// so they never reach the API as a path variable (backend expects a numeric jobId).
const normalizeRouteId = (value) => {
  const text = String(value ?? '').trim()
  return text && text !== 'undefined' && text !== 'null' ? text : ''
}
const jobId = computed(() => normalizeRouteId(route.params.jobId))

const loading = ref(true)
const loadError = ref('')
const run = ref(null)
const events = ref([])
const eventLogRef = ref(null)
const cancelling = ref(false)
const deleting = ref(false)
const rerunning = ref(false)
const selectedRerunStep = ref('')
let pollTimer = null

const isActive = computed(() =>
  ['SUBMITTING', 'SUBMITTED', 'RUNNING'].includes(run.value?.jobStatus)
)

const showCmakerStatus = computed(() => {
  const cmakerStatus = String(run.value?.cmakerStatus || '').trim()
  const jobStatus = String(run.value?.jobStatus || '').trim()
  return cmakerStatus && cmakerStatus.toLowerCase() !== jobStatus.toLowerCase()
})

// cmaker rerun-step is offered for terminal-but-incomplete runs.
const canRerun = computed(() => ['FAILED', 'CANCELLED'].includes(run.value?.jobStatus))

// Delete is offered only once the run has reached a terminal state (matches cmaker's guard).
const isTerminal = computed(() =>
  ['SUCCEEDED', 'FAILED', 'CANCELLED', 'SKIPPED'].includes(run.value?.jobStatus)
)

// Steps the run actually executed (parsed from the archived step events) —
// the valid choices for cmaker's rerun-step.
const availableSteps = computed(() => {
  const steps = []
  for (const e of events.value) {
    let step = null
    try { step = JSON.parse(e.dataJson || '{}').step } catch { step = null }
    if (step && !steps.includes(step)) steps.push(step)
  }
  return steps
})

watch(availableSteps, steps => {
  if (selectedRerunStep.value && steps.includes(selectedRerunStep.value)) return
  if (!steps.length) { selectedRerunStep.value = ''; return }
  const failed = run.value?.failedStep
  // Default to the failed step, otherwise the last step the run reached.
  selectedRerunStep.value = failed && steps.includes(failed) ? failed : steps[steps.length - 1]
})

const feedsArray = computed(() => {
  const snap = run.value?.feedsSnapshot
  if (!snap) return []
  if (Array.isArray(snap)) return snap
  return []
})

function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function formatDuration(start, end) {
  if (!start) return '—'
  const startDate = new Date(start)
  const finishDate = end ? new Date(end) : new Date()
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(finishDate.getTime())) return '—'
  const seconds = Math.max(0, Math.floor((finishDate - startDate) / 1000))
  if (seconds < 60) return `${seconds} сек`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} мин ${seconds % 60} сек`
  return `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`
}

function pretty(v) {
  if (v == null) return ''
  if (typeof v === 'string') {
    try { return JSON.stringify(JSON.parse(v), null, 2) } catch { return v }
  }
  try { return JSON.stringify(v, null, 2) } catch { return String(v) }
}

// Live updates use polling (reliable cross-origin, no SSE/EventSource quirks):
// while the run is active we re-fetch status + the step log every few seconds,
// and stop once it reaches a terminal state.
const POLL_INTERVAL_MS = 4000

async function fetchEventsSafe({ preserveScroll = false } = {}) {
  const previousScrollTop = preserveScroll && eventLogRef.value
    ? eventLogRef.value.scrollTop
    : null
  try {
    events.value = await getRunEvents(jobId.value)
    if (previousScrollTop != null) {
      await nextTick()
      if (eventLogRef.value) eventLogRef.value.scrollTop = previousScrollTop
    }
  } catch (e) {
    console.warn('run events fetch failed', e)
  }
}

async function loadRun() {
  loading.value = true
  loadError.value = ''
  if (!jobId.value) {
    // No valid id in the route (e.g. /cmaker/runs/undefined) — don't fire any request
    // and never start polling with a bad id.
    loadError.value = 'Run не найден.'
    loading.value = false
    return
  }
  try {
    run.value = await getRun(jobId.value)
    await fetchEventsSafe()
    scrollLogBottom()
    if (isActive.value) startPolling()
    else stopPolling()
  } catch (e) {
    console.error(e)
    loadError.value = e?.response?.status === 404 ? 'Run не найден.' : 'Не удалось загрузить run.'
  } finally {
    loading.value = false
  }
}

async function refresh() {
  try {
    run.value = await getRun(jobId.value)
  } catch (e) {
    console.warn('run refresh failed', e)
    return
  }
  await fetchEventsSafe({ preserveScroll: true })
  if (!isActive.value) stopPolling()
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(refresh, POLL_INTERVAL_MS)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function scrollLogBottom() {
  nextTick(() => {
    if (eventLogRef.value) {
      eventLogRef.value.scrollTop = eventLogRef.value.scrollHeight
    }
  })
}

async function onRerun() {
  if (!selectedRerunStep.value || rerunning.value) return
  if (!confirm(`Перезапустить run с шага «${selectedRerunStep.value}»?`)) return
  rerunning.value = true
  try {
    await rerunStep(jobId.value, selectedRerunStep.value)
    events.value = []
    await loadRun() // status is RUNNING now → loadRun starts polling again
  } catch (e) {
    console.error(e)
    alert(e?.response?.data?.message || 'Не удалось перезапустить шаг.')
  } finally {
    rerunning.value = false
  }
}

async function onCancel() {
  if (!confirm('Отменить активный run?')) return
  cancelling.value = true
  try {
    await cancelRun(jobId.value)
    stopPolling()
    await loadRun()
  } catch (e) {
    console.error(e)
    alert('Не удалось отменить run.')
  } finally {
    cancelling.value = false
  }
}

async function onDelete() {
  if (!confirm('Удалить run и все его артефакты? Действие необратимо.')) return
  deleting.value = true
  try {
    await deleteRun(jobId.value)
    stopPolling()
    router.push('/cmaker/runs')
  } catch (e) {
    console.error(e)
    alert(e?.response?.data?.message || 'Не удалось удалить run.')
  } finally {
    deleting.value = false
  }
}

onMounted(loadRun)
onBeforeUnmount(stopPolling)
</script>

<style scoped>
.cmaker-run-detail {
  display: flex;
  flex-direction: column;
  height: calc(var(--app-vh, 1vh) * 100 - 2px);
  padding: 8px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
  overflow: hidden;
  box-sizing: border-box;
  gap: 8px;
}

.page-hero {
  flex: 0 0 auto;
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.page-hero__title { font-weight: 800; }
.page-hero__subtitle { color: rgba(232, 242, 255, 0.92); font-size: 13px; }
.page-hero__back { color: #103f77; border-color: #cfe2ff; font-weight: 700; }

.detail-grid {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-card {
  flex: 0 0 auto;
  background: #ffffff;
  border: 1px solid #dfe7f5;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}
.summary-card { gap: 12px; }
.summary-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.summary-card__tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.summary-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 7px;
  border: 1px solid #dfe7f5;
  border-radius: 999px;
  background: #f7f9fd;
  color: #56657d;
  font-size: 11px;
  font-weight: 600;
}
.summary-tag i { color: #6681aa; font-size: 11px; }
.summary-card__status { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }

.feeds-wrap {
  max-height: clamp(140px, 28vh, 240px);
  overflow: auto;
  border: 1px solid #eef2f9;
  border-radius: 8px;
}
.feeds-wrap table { font-size: 12px; }
.feeds-wrap td { word-break: break-word; }

.generation-cloud-title {
  display: flex;
  align-items: center;
  color: #234574;
  font-size: 13px;
  font-weight: 700;
}

.meta-label { font-size: 11px; color: #4d5f82; text-transform: uppercase; font-weight: 700; display: block; }
.summary-id {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 10px;
  background: #f7f9fd;
  border: 1px solid #e4eaf4;
  border-radius: 6px;
}
.summary-id__icon { flex: 0 0 auto; color: #6c7a92; font-size: 14px; }
.summary-id .meta-label { flex: 0 0 auto; }
.summary-id__value {
  min-width: 0;
  overflow: hidden;
  color: #34435b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-details {
  display: grid;
  grid-template-columns: minmax(210px, 280px) minmax(0, 1fr);
  gap: 18px;
  align-items: stretch;
}
.summary-timing { padding-right: 18px; border-right: 1px solid #e8eef8; }
.timing-list { display: flex; flex-direction: column; gap: 1px; margin-top: 2px; }
.timing-row { display: flex; gap: 8px; justify-content: space-between; color: #56657d; font-size: 11px; line-height: 1.35; }
.timing-row span { color: #7c899d; }
.timing-row time { color: #34435b; white-space: nowrap; }
.summary-metrics { display: flex; align-items: center; gap: 0; min-width: 0; flex-wrap: wrap; }
.summary-metric {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 4px 18px;
  border-right: 1px solid #e8eef8;
}
.summary-metric:first-child { padding-left: 0; }
.summary-metric:last-child { border-right: 0; }
.summary-metric__icon { color: #6681aa; font-size: 17px; }
.summary-metric__value { margin-top: 1px; color: #293a55; font-size: 14px; font-weight: 700; white-space: nowrap; }
.summary-metric__unit { color: #7c899d; font-size: 11px; font-weight: 600; }
.summary-metric--action { border-right: 0; }
.summary-metric--action .btn { white-space: nowrap; }
.summary-rerun {
  flex: 1 1 340px;
  min-width: 280px;
  padding: 4px 18px;
  border-right: 1px solid #e8eef8;
}
.summary-error {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 10px;
  border-left: 3px solid #dc3545;
  background: #fff8f8;
  color: #a4262c;
  font-size: 12px;
}

.summary-actions {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid #e8eef8;
}
.rerun-controls { display: flex; gap: 8px; align-items: center; }
.rerun-select { max-width: 220px; }
.rerun-button { white-space: nowrap; }

.failure-card { border-left: 3px solid #dc3545; }
.skip-card { border-left: 3px solid #d79b15; background: #fffdf8; }
.snapshot-card {
  display: block;
  flex: 0 0 auto;
  width: 100%;
  overflow: hidden;
}
.snapshot-content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.snapshot-card__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  list-style: none;
}
.snapshot-card__summary::-webkit-details-marker { display: none; }
.snapshot-card__chevron { color: #6681aa; font-size: 13px; transition: transform 160ms ease; }
.snapshot-card[open] .snapshot-card__chevron { transform: rotate(180deg); }
.snapshot-card__content { padding-top: 12px; }
.snapshot-pane {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e8eef8;
  border-radius: 6px;
  background: #fbfcff;
}
.snapshot-pane__title { margin-bottom: 8px; color: #4d5f82; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.snapshot-pane .json-block { max-height: clamp(140px, 28vh, 280px); }
.event-card {
  flex: 1 1 360px;
  min-height: 96px;
}

.json-block {
  background: #f6f8fd;
  border: 1px solid #e3e9f5;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11.5px;
  max-height: 280px;
  overflow: auto;
  margin: 0;
}

.event-log {
  flex: 1 1 auto;
  background: #fafcff;
  border: 1px solid #e3e9f5;
  border-radius: 8px;
  padding: 8px 10px;
  min-height: 0;
  overflow-y: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
}

.event-row + .event-row { margin-top: 6px; padding-top: 6px; border-top: 1px dashed #e3e9f5; }
.event-row__head { display: flex; gap: 12px; align-items: baseline; }
.event-row__name { font-weight: 700; color: #2f4f88; }
.event-row__time { font-size: 11px; }
.event-row__data { margin: 4px 0 0; white-space: pre-wrap; word-break: break-word; }

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: #eef3ff;
  color: #2f4f88;
  border: 1px solid #d8e5fb;
}
.status-badge--succeeded { background: #e8f7ee; color: #1c7a3a; border-color: #b8e0c6; }
.status-badge--failed { background: #fdecec; color: #a4262c; border-color: #f4bcbf; }
.status-badge--cancelled { background: #f3f5f9; color: #4f5b75; border-color: #d6dbe5; }
.status-badge--skipped { background: #eef3ff; color: #2f4f88; border-color: #d8e5fb; }
.status-badge--running, .status-badge--submitted, .status-badge--submitting { background: #fff7e0; color: #8b6608; border-color: #f1dc9c; }

.card-load-state {
  display: flex; justify-content: center; align-items: center; padding: 40px;
}

@media (max-width: 920px) {
  .summary-details { grid-template-columns: 1fr; gap: 10px; }
  .summary-timing { padding: 0 0 10px; border-right: 0; border-bottom: 1px solid #e8eef8; }
}

@media (max-width: 560px) {
  .page-hero { align-items: flex-start; }
  .summary-card__head { flex-direction: column; gap: 8px; }
  .summary-card__status { justify-content: flex-start; }
  .summary-id { align-items: flex-start; flex-wrap: wrap; }
  .summary-id__value { flex: 0 0 100%; }
  .summary-metrics { align-items: stretch; flex-direction: column; }
  .summary-metric { width: 100%; padding: 7px 0; border-right: 0; border-bottom: 1px solid #e8eef8; }
  .summary-metric:last-child { border-bottom: 0; }
  .summary-error { align-items: flex-start; flex-direction: column; gap: 6px; }
  .summary-rerun { min-width: 0; width: 100%; padding: 7px 0; border-right: 0; border-bottom: 1px solid #e8eef8; }
  .snapshot-content-grid { grid-template-columns: minmax(0, 1fr); }
  .rerun-controls, .rerun-select { width: 100%; max-width: none; }
  .rerun-controls { align-items: stretch; flex-direction: column; }
}
</style>
